import { useState } from 'react'
import { toast } from 'react-toastify'

import type { Selectable } from '@loadsmart/loadsmart-ui/dist/hooks/useSelectable'
import type EventLike from '@loadsmart/loadsmart-ui/dist/utils/types/EventLike'

import { mapToOptions, type Option } from 'core/domain/Option'
import { formatDate, parseISO } from 'core/utils/date'

import { isCertificateOfInsurance, isDocumentValid, type Document } from '../../../domain/Document'
import { useAttachmentDocumentTypes } from '../../../hooks/useAttachmentDocumentTypes'
import {
  getUploadErrorMessage,
  allowedFileExtensions,
} from '../../../presenters/shipperDocumentUploadPresenter'
import {
  uploadShipperDocument,
  type UploadShipperDocumentParams,
} from '../../../use-cases/uploadShipperDocument'
import { SHIPPER_DETAIL_DRAWER_TOAST_ID } from '../ShipperDetailDrawer'

type UseUploadDocumentDialogParams = {
  shipperUUID: string
  onClose: () => void
}

export function useUploadDocumentDialog({ shipperUUID, onClose }: UseUploadDocumentDialogParams) {
  const [document, setDocument] = useState<Document>({});
  const [errorMessage, setErrorMessage] = useState<string>('')
  const [isUploading, setIsUploading] = useState(false)

  const { documentTypes } = useAttachmentDocumentTypes()

  const typeOptions = mapToOptions({
    values: documentTypes,
    getLabel: type => type.label,
    getValue: type => type.value,
  })

  const currentTypeOption = typeOptions.find(option => option.value === document.type) || null
  const isCOI = isCertificateOfInsurance(document)

  function canUpload() {
    if (errorMessage) return false
    if (isUploading) return false
    if (!isDocumentValid(document)) return false;
    return true
  }

  async function handleUploadClick() {
    if (!canUpload()) return
    setIsUploading(true)
    try {
      const params: UploadShipperDocumentParams = {
        shipperUuid: shipperUUID,
        document,
      }
      await uploadShipperDocument(params)
      setDocument({});
      setErrorMessage('')
      onClose()
      toast.success('File uploaded successfully', { containerId: SHIPPER_DETAIL_DRAWER_TOAST_ID })
    } catch {
      toast.error('Failed to upload file', { containerId: SHIPPER_DETAIL_DRAWER_TOAST_ID })
    } finally {
      setIsUploading(false)
    }
  }

  function handleDocumentTypeChange(e: EventLike<Selectable | Selectable[] | null>) {
    const option = e?.target?.value as Option<string> | null
    const value = option?.value
    setDocument({...document, type: value})
  }

  function handleDateChange(e: EventLike<string | null>) {
    const isoString = e.target.value
    if (isoString) {
      const newDate = formatDate(parseISO(isoString), 'yyyy-MM-dd')
      setDocument({...document, expirationDate: newDate})
    } else {
      setDocument({...document, expirationDate: undefined})
    }
  }

  function handleFileRemoved() {
    setDocument({...document, file: undefined})
  }

  function handleFilesAdded(files: File[]) {
    if (!files.length) return
    const newErrorMessage = getUploadErrorMessage(files)
    setErrorMessage(newErrorMessage)
    if (newErrorMessage) return
    setDocument({...document, file: files[0]})
  }

  return {
    document,
    errorMessage,
    isUploading,
    typeOptions,
    currentTypeOption,
    isCOI,
    allowedFileExtensions,
    canUpload,
    handleUploadClick,
    handleDocumentTypeChange,
    handleDateChange,
    handleFileRemoved,
    handleFilesAdded,
  }
}
